import React, { useState } from 'react';

import { Button, Paper, Stack, TextField, Typography } from '@mui/material';

import { RoomBulk, RoomField, fromPercent } from './nativeTourPricing';

interface Props {
  roomTypes: Array<string>;
  shownCount: number;
  onApply: (bulk: Record<string, RoomBulk>) => void;
}

const FIELDS: Array<{ field: RoomField; label: string }> = [
  { field: 'discount', label: 'Discount %' },
  { field: 'markup', label: 'Markup %' },
  { field: 'luxPlusDiscount', label: 'LUX+ %' },
];

/** V2's bulk update: what's entered goes on that room in every departure shown; blanks leave theirs. */
export default function NativeTourPricingBulkApply({ roomTypes, shownCount, onApply }: Props) {
  const [values, setValues] = useState<Record<string, Partial<Record<RoomField, string>>>>({});

  const bulk = Object.fromEntries(
    Object.entries(values).map(([roomType, fields]) => [
      roomType,
      Object.fromEntries(
        Object.entries(fields)
          .map(([field, value]) => [field, fromPercent(value ?? '')])
          .filter(([, value]) => value !== null),
      ) as RoomBulk,
    ]),
  );
  const hasValues = Object.values(bulk).some((fields) => Object.keys(fields).length > 0);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="subtitle2">Bulk update the {shownCount} departures shown</Typography>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems={{ lg: 'flex-end' }}>
          {roomTypes.map((roomType) => (
            <Stack key={roomType} spacing={1}>
              <Typography variant="caption" color="text.secondary">
                {roomType}
              </Typography>
              <Stack direction="row" spacing={1}>
                {FIELDS.map(({ field, label }) => (
                  <TextField
                    key={field}
                    size="small"
                    type="number"
                    label={label}
                    value={values[roomType]?.[field] ?? ''}
                    onChange={(event) =>
                      setValues({ ...values, [roomType]: { ...values[roomType], [field]: event.target.value } })
                    }
                    inputProps={{ min: 0, step: 1 }}
                    sx={{ width: 110 }}
                  />
                ))}
              </Stack>
            </Stack>
          ))}
          <Button
            variant="contained"
            disabled={!hasValues || shownCount === 0}
            onClick={() => {
              onApply(bulk);
              setValues({});
            }}
          >
            Apply to {shownCount}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}
